import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plane, Bus, Clock, MapPin, Check, X, 
  ChevronLeft, ExternalLink, Calendar, Users,
  ChevronDown, ChevronRight, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getPackageById, logRedirect } from '../lib/api';
import { PackageCard } from '../components/PackageCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Accordion Item Component
const AccordionItem = ({ title, icon: Icon, iconColor, isOpen, onClick, children }) => {
  return (
    <div className="glass rounded-2xl overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
        data-testid={`accordion-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`w-5 h-5 ${iconColor || 'text-gold'}`} />}
          <h3 className="font-heading font-semibold text-lg text-white">
            {title}
          </h3>
        </div>
        <ChevronDown 
          className={`w-5 h-5 text-white/60 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 pt-2">
          {children}
        </div>
      </div>
    </div>
  );
};

// Day Accordion Item Component - for individual days in the program
const DayAccordionItem = ({ day, index, isOpen, onClick }) => {
  // Extract just the number from day label (e.g., "Ziua 1" -> "1", "ZIUA 2" -> "2")
  const extractDayNumber = (dayStr) => {
    if (!dayStr) return index + 1;
    const match = dayStr.match(/\d+/);
    return match ? match[0] : index + 1;
  };
  
  const dayNumber = extractDayNumber(day.day);
  const dayTitle = typeof day === 'string' ? day : day.title;
  const dayLabel = day.day || `Ziua ${index + 1}`;
  
  return (
    <div className="bg-white/5 rounded-xl overflow-hidden mb-2">
      <button
        onClick={onClick}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors duration-200 text-left"
        data-testid={`day-accordion-${index + 1}`}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
          <span className="text-gold font-bold text-sm">{dayNumber}</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-gold text-xs font-medium uppercase tracking-wider">{dayLabel}</span>
          <h4 className="text-white font-semibold text-sm truncate">{dayTitle}</h4>
        </div>
        <ChevronRight 
          className={`w-5 h-5 text-white/40 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-90' : ''}`} 
        />
      </button>
      
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4 pt-2 border-t border-white/10">
          {typeof day === 'string' ? (
            <p className="text-white/80 text-sm leading-relaxed">{day}</p>
          ) : (
            <>
              {day.description && (
                <p className="text-white/80 text-sm leading-relaxed mb-4">{day.description}</p>
              )}
              <div className="flex flex-wrap gap-4 text-xs">
                {day.accommodation && (
                  <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg">
                    <MapPin className="w-4 h-4 text-gold" />
                    <div>
                      <span className="text-white/50 block text-xs">Cazare</span>
                      <span className="text-white/90">{day.accommodation}</span>
                    </div>
                  </div>
                )}
                {day.meals && (
                  <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <div>
                      <span className="text-white/50 block text-xs">Mese incluse</span>
                      <span className="text-white/90">{day.meals}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function PackageDetailPage({ useSeoUrl = false }) {
  const { id, destinationSlug, slug } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similarPackages, setSimilarPackages] = useState([]);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [openDay, setOpenDay] = useState(null);

  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  const toggleDay = (dayIndex) => {
    setOpenDay(openDay === dayIndex ? null : dayIndex);
  };

  useEffect(() => {
    const loadPackage = async () => {
      try {
        let data;
        if (useSeoUrl && destinationSlug && slug) {
          // Fetch by SEO slug
          const response = await fetch(`${API_URL}/api/seo/package/${destinationSlug}/${slug}`);
          if (!response.ok) throw new Error('Package not found');
          data = await response.json();
        } else {
          // Fetch by ID (legacy)
          data = await getPackageById(id);
        }
        setPkg(data);
      } catch (error) {
        console.error('Error loading package:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPackage();
    window.scrollTo(0, 0);
  }, [id, destinationSlug, slug, useSeoUrl]);

  // Load similar packages + track view
  useEffect(() => {
    if (!pkg?.id) return;
    // Track view detail
    fetch(`${API_URL}/api/track/view-detail?package_id=${encodeURIComponent(pkg.id)}&package_title=${encodeURIComponent(pkg.title)}`, { method: 'POST' }).catch(() => {});
    // Load similar
    fetch(`${API_URL}/api/packages/${pkg.id}/similar?limit=8`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setSimilarPackages(d))
      .catch(() => {});
  }, [pkg?.id]);

  // Update document title and meta tags
  useEffect(() => {
    if (pkg?.title) {
      document.title = `${pkg.title} | VaAi`;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pkg.short_description || 'Descoperă cele mai bune oferte de călătorie cu VaAi.');
      }
    }
    return () => {
      document.title = 'VaAi - Călătorește Inteligent';
    };
  }, [pkg]);

  const handleReserve = async () => {
    if (!pkg) return;
    try {
      await logRedirect(pkg.id, pkg.title, pkg.operator, pkg.category || '', 'package_detail');
    } catch (err) {
      console.error('[PackageDetail] Redirect tracking failed:', err);
    }
    window.open(pkg.redirect_link, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center">
        <p className="text-white/60 text-lg mb-4">Pachetul nu a fost găsit</p>
        <Button onClick={() => navigate('/')} className="btn-outline">
          Înapoi la pagina principală
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16" data-testid="package-detail-page">
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img
          src={pkg.image_url}
          alt={pkg.title}
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-24 left-4 md:left-8 z-10 glass px-4 py-2 rounded-full flex items-center gap-2 text-white hover:bg-white/20 transition-all"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          Înapoi
        </button>

        {/* Badges */}
        <div className="absolute top-24 right-4 md:right-8 z-10 flex flex-col gap-2 items-end">
          {pkg.is_popular && (
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 rounded-full text-sm font-bold text-white shadow-lg flex items-center gap-1.5" data-testid="detail-popular-badge">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Popular
            </span>
          )}
          {pkg.created_at && (Date.now() - new Date(pkg.created_at).getTime()) / (1000 * 60 * 60 * 24) <= 7 && (
            <span className="bg-emerald-500 px-3 py-1.5 rounded-full text-sm font-bold text-white shadow-lg" data-testid="detail-new-badge">
              Nou
            </span>
          )}
          {pkg.is_early_booking && (
            <span className="badge-early-booking px-3 py-1.5 rounded-full text-sm font-semibold text-white">
              Early Booking
            </span>
          )}
          {pkg.is_last_minute && (
            <span className="badge-last-minute px-3 py-1.5 rounded-full text-sm font-semibold text-white">
              Last Minute
            </span>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title Card */}
            <div className="glass rounded-2xl p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {pkg.category !== 'Concerte' && (
                <div className="glass px-3 py-1 rounded-lg flex items-center gap-1 text-sm text-white/80">
                  {pkg.transport_type === 'avion' ? (
                    <Plane className="w-4 h-4 text-gold" />
                  ) : (
                    <Bus className="w-4 h-4 text-gold" />
                  )}
                  {pkg.transport_type === 'avion' ? 'Avion' : pkg.transport_type === 'individual' ? 'Individual' : 'Autocar'}
                </div>
                )}
                {pkg.category === 'Concerte' && pkg.music_genre && (
                <div className="px-3 py-1 rounded-lg bg-purple-600/80 flex items-center gap-1 text-sm text-white">
                  {pkg.music_genre}
                </div>
                )}
                {pkg.category === 'Concerte' && pkg.venue && (
                <div className="glass px-3 py-1 rounded-lg text-sm text-white/80">
                  {pkg.venue}{pkg.city ? `, ${pkg.city}` : ''}
                </div>
                )}
                <div className="glass px-3 py-1 rounded-lg flex items-center gap-1 text-sm text-white/80">
                  <Clock className="w-4 h-4 text-gold" />
                  {pkg.category === 'Concerte' && pkg.event_date
                    ? `${new Date(pkg.event_date).toLocaleDateString('ro-RO', {day:'numeric',month:'long',year:'numeric'})}${pkg.event_time ? ` | ${pkg.event_time}` : ''}`
                    : pkg.duration}
                </div>
              </div>

              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
                {pkg.title}
              </h1>
              
              <p className="text-white/70 text-lg mb-4">
                {pkg.short_description}
              </p>

              <div className="flex items-center gap-2 text-white/60">
                <span>Vândut de</span>
                <span className="text-gold font-semibold">{pkg.operator}</span>
              </div>
            </div>

            {/* Dates */}
            {pkg.departure_dates?.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-lg text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  Date de plecare disponibile
                </h3>
                <div className="flex flex-wrap gap-2">
                  {pkg.departure_dates.map((date, i) => (
                    <span key={i} className="bg-white/10 px-4 py-2 rounded-xl text-white/80 text-sm">
                      {date}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Includes / Excludes */}
            {/* Accordion Sections */}
            <div className="space-y-3">
              {/* Include */}
              {pkg.includes?.length > 0 && (
                <AccordionItem
                  title="Include"
                  icon={Check}
                  iconColor="text-emerald-400"
                  isOpen={openAccordion === 'includes'}
                  onClick={() => toggleAccordion('includes')}
                >
                  <ul className="space-y-2">
                    {pkg.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {pkg.includes_note && (
                    <p className="mt-4 text-white/60 text-xs italic border-t border-white/10 pt-3">
                      {pkg.includes_note}
                    </p>
                  )}
                </AccordionItem>
              )}

              {/* Servicii suplimentare */}
              {pkg.additional_services?.length > 0 && (
                <AccordionItem
                  title="Servicii suplimentare (se achită în agenție)"
                  icon={Users}
                  iconColor="text-blue-400"
                  isOpen={openAccordion === 'additional'}
                  onClick={() => toggleAccordion('additional')}
                >
                  <div className="space-y-4">
                    {pkg.additional_services.map((service, i) => (
                      <div key={i} className="bg-white/5 rounded-xl p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-white font-medium">{service.name}</span>
                          <span className="text-gold font-semibold">{service.price}</span>
                        </div>
                        {service.note && (
                          <p className="text-white/50 text-xs italic mb-2">({service.note})</p>
                        )}
                        {service.includes?.length > 0 && (
                          <ul className="space-y-1 mt-2 pl-2 border-l-2 border-gold/30">
                            {service.includes.map((item, j) => (
                              <li key={j} className="text-white/70 text-sm flex items-start gap-2">
                                <span className="text-gold">•</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionItem>
              )}

              {/* Nu include */}
              {pkg.excludes?.length > 0 && (
                <AccordionItem
                  title="Nu include"
                  icon={X}
                  iconColor="text-red-400"
                  isOpen={openAccordion === 'excludes'}
                  onClick={() => toggleAccordion('excludes')}
                >
                  <ul className="space-y-2">
                    {pkg.excludes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                        <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </AccordionItem>
              )}

              {/* Program detaliat */}
              {pkg.detailed_program?.length > 0 && (
                <AccordionItem
                  title={`Program detaliat (${pkg.detailed_program.length} zile)`}
                  icon={Calendar}
                  iconColor="text-gold"
                  isOpen={openAccordion === 'program'}
                  onClick={() => toggleAccordion('program')}
                >
                  <div className="space-y-2">
                    {pkg.detailed_program.map((day, i) => (
                      <DayAccordionItem
                        key={i}
                        day={day}
                        index={i}
                        isOpen={openDay === i}
                        onClick={() => toggleDay(i)}
                      />
                    ))}
                  </div>
                </AccordionItem>
              )}

              {/* Excursii opționale */}
              {pkg.optional_excursions?.length > 0 && (
                <AccordionItem
                  title="Excursii opționale"
                  icon={MapPin}
                  iconColor="text-gold"
                  isOpen={openAccordion === 'excursions'}
                  onClick={() => toggleAccordion('excursions')}
                >
                  <div className="space-y-2">
                    {pkg.optional_excursions.map((exc, i) => (
                      <div key={i} className="bg-white/5 rounded-xl p-4 flex justify-between items-start gap-4">
                        <span className="text-white/80 text-sm flex-1">{exc.name}</span>
                        <span className="text-gold font-semibold text-sm whitespace-nowrap">{exc.price}</span>
                      </div>
                    ))}
                  </div>
                  {pkg.optional_excursions_note && (
                    <p className="mt-4 text-white/60 text-xs italic border-t border-white/10 pt-3">
                      {pkg.optional_excursions_note}
                    </p>
                  )}
                </AccordionItem>
              )}

              {/* Informații Utile */}
              {pkg.useful_info && (
                <AccordionItem
                  title="Informații Utile"
                  icon={Users}
                  iconColor="text-blue-400"
                  isOpen={openAccordion === 'useful'}
                  onClick={() => toggleAccordion('useful')}
                >
                  <div className="text-white/80 text-sm whitespace-pre-line leading-relaxed">
                    {pkg.useful_info}
                  </div>
                </AccordionItem>
              )}
            </div>
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 glass rounded-2xl p-6">
              {/* Price */}
              <div className="text-center mb-6">
                <span className="text-white/50 text-xs uppercase tracking-wide block mb-1">de la</span>
                {pkg.old_price && (
                  <span className="text-white/40 line-through text-lg block mb-1">
                    {pkg.old_price}
                  </span>
                )}
                <span className="text-gold font-bold text-4xl">
                  {pkg.price}
                </span>
                <span className="text-white/60 text-sm block">/persoană</span>
              </div>

              {/* Reserve Button */}
              <Button
                onClick={handleReserve}
                className="w-full btn-gold py-4 text-lg mb-4 flex items-center justify-center gap-2"
                data-testid="detail-reserve-btn"
              >
                Rezervă acum
                <ExternalLink className="w-4 h-4" />
              </Button>

              <p className="text-white/50 text-xs text-center">
                Vei fi redirecționat către site-ul {pkg.operator}
              </p>

              {/* Quick Info */}
              <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                <div className="flex items-center gap-3 text-white/70 text-sm">
                  <Clock className="w-4 h-4 text-gold" />
                  {pkg.duration}
                </div>
                {pkg.category !== 'Concerte' && (
                <div className="flex items-center gap-3 text-white/70 text-sm">
                  {pkg.transport_type === 'avion' ? (
                    <Plane className="w-4 h-4 text-gold" />
                  ) : (
                    <Bus className="w-4 h-4 text-gold" />
                  )}
                  Transport: {pkg.transport_type === 'avion' ? 'Avion' : pkg.transport_type === 'individual' ? 'Individual' : 'Autocar'}
                </div>
                )}
                {pkg.category === 'Concerte' && pkg.venue && (
                <div className="flex items-center gap-3 text-white/70 text-sm">
                  <MapPin className="w-4 h-4 text-gold" />
                  {pkg.venue}{pkg.city ? `, ${pkg.city}` : ''}
                </div>
                )}
                <div className="flex items-center gap-3 text-white/70 text-sm" data-testid="package-views-info">
                  <Users className="w-4 h-4 text-gold" />
                  {(() => {
                    const vc = pkg.view_count || 0;
                    if (vc === 1) return 'Pachetul a fost accesat de 1 persoană';
                    // Romanian plural: 2-19 → "X persoane", 20+ → "X de persoane"
                    const last2 = vc % 100;
                    const usesDe = vc >= 20 && (last2 === 0 || last2 >= 20);
                    return `Pachetul a fost accesat de ${vc}${usesDe ? ' de' : ''} persoane`;
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Packages Section */}
      {similarPackages.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-white mb-6">Programe similare</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-4 md:overflow-visible md:snap-none">
            {similarPackages.map(sp => (
              <div key={sp.id} className="min-w-[280px] snap-start md:min-w-0">
                <PackageCard package={sp} useSeoUrl={true} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
